import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { UpdatePoint, UpdatePoints } from './points.actions';

import { ResponseErrorService } from '../errors/response-error.service';

import { CommentApiProviderService, PointsApiProviderService } from '@core/api';
import { TPointUpdate } from '@project/view-models';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TNewComment } from '../../data-providers/api-providers/comment-api-provider/comment-requests.model';
import { TUpdatePointsRequest } from '../../data-providers/api-providers/points-api-provider/points-requests.model';
import { logEventInGTAG } from '../../services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from '../../services/analytics/google-analytics.consts';
import { TBulkCustomFieldsExtended } from '../custom-fields/bulk-custom-fields-modal/bulk-custom-fields.model';
import { ECustomFieldType } from '../custom-fields/custom-field-types-enums';
import { logErrorInSentry } from '../errors/response-error';
import { GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL } from '../site/site-table/columns/columns.store';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import { TCommentData } from './comment-response.model';
import { PointsGenerateService } from './point-generate/points-generate.service';
import { TPoint } from './points.model';
import { PointsService } from './points.service';

@Injectable({
  providedIn: 'root',
})
export class PointsUpdateService {
  public readonly pointsUpdated$ = new Subject<string[]>();

  constructor(
    private store: Store,
    private responseErrorService: ResponseErrorService,
    private commentApiProviderService: CommentApiProviderService,
    private pointsApiProviderService: PointsApiProviderService,
    private pointsGenerateService: PointsGenerateService,
    private customTableService: CustomTableService,
    private pointsService: PointsService,
  ) {}

  updatePoints(
    pointIds: string[],
    changes: Record<string, unknown>,
    mentions: string[] = [],
  ): Observable<null> {
    const body: TUpdatePointsRequest = {
      pointIds,
      ...changes,
      mentions,
    };

    return this.pointsApiProviderService.updatePoints(body).pipe(
      map((response) => {
        const points = response.map((pointResponse) =>
          this.pointsGenerateService.generatePoint(pointResponse),
        );

        this.store.dispatch(
          new UpdatePoints({
            changedPoints: points,
          }),
        );

        this.customTableService.clearGroupSizes();

        this.customTableService.reloadTable();

        this.pointsUpdated$.next(pointIds);

        return null;
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to update points'),
      ),
    );
  }

  updatePointsCustomFields(
    pointIds: string[],
    customFields: TBulkCustomFieldsExtended,
    mentions?: string[],
  ): Observable<null> {
    const customFieldsMap = Object.keys(customFields)
      .filter((key) => {
        if (customFields[key].value !== undefined && customFields[key].value !== null) {
          return true;
        }

        return false;
      })
      .map((key) => {
        if (customFields[key].type === ECustomFieldType.LIST) {
          return {
            customFieldTemplateId: key,
            value: customFields[key].idOfChosenElement,
          };
        }
        if (customFields[key].type === ECustomFieldType.MULTI_LIST) {
          return {
            customFieldTemplateId: key,
            selectedItemIds: customFields[key].idOfChosenElements,
          };
        } else if (customFields[key].type !== ECustomFieldType.RICHTEXT) {
          return {
            customFieldTemplateId: key,
            value: customFields[key].value.toString(),
          };
        } else {
          return {
            customFieldTemplateId: key,
            value: customFields[key].value,
          };
        }
      });

    const customFieldDataToSend = { customFieldsList: customFieldsMap };

    return this.updatePoints(pointIds, customFieldDataToSend, mentions).pipe(
      catchError((error) => {
        logErrorInSentry({ error, message: 'Failed to update points custom fields' });

        return throwError(error);
      }),
    );
  }

  addComment(_id: string, pointComment: TNewComment): Observable<TCommentData> {
    return this.commentApiProviderService
      .addComment(_id, pointComment)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to add comment'),
        ),
      );
  }

  editComment(commentId: string, pointComment: TNewComment): Observable<TCommentData> {
    return this.commentApiProviderService
      .editComment(commentId, pointComment)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to edit comment'),
        ),
      );
  }

  updatePointField(pointId: string, body: TPointUpdate): Observable<TPoint> {
    if (body.pins) {
      logEventInGTAG(EGoogleEventSite.SITE__POINT__PIN_CHANGE, {
        source: EGoogleEventCategory.SITE,
      });
    }

    return this.pointsApiProviderService
      .updatePointField(pointId, body)
      .pipe(
        map((response) => {
          const existing = this.pointsService.findPoint(pointId);
          const mergedResponse = existing?.customFieldsMap
            ? {
                ...response,
                customFieldsMap: {
                  ...existing.customFieldsMap,
                  ...(response.customFieldsMap ?? {}),
                },
              }
            : response;

          const point = this.pointsGenerateService.generatePoint(mergedResponse);

          this.store.dispatch(
            new UpdatePoint({
              workspaceId: point.workspaceRef.id,
              point,
            }),
          );

          this.customTableService.clearGroupSizes();

          this.customTableService.reloadTable({ preserveTableState: true });

          if (body.customFieldsList) {
            const displaySumColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL();
            const cfIdsUpdated = body.customFieldsList.map((cf) => cf.customFieldTemplateId);

            const match = cfIdsUpdated.filter((cfId) =>
              displaySumColumns.find((column) => column.customFieldIds.includes(cfId)),
            );

            if (match.length) {
              const formattedMatch = match.map((val) => ({ key: val, customFields: [val] }));
              this.customTableService.fetchColumnSums(formattedMatch, { invalidateCache: true });
            }
          }

          return point;
        }),
      )
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update point field'),
        ),
      );
  }
}
